import React, { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import LockResetIcon from '@mui/icons-material/LockReset';
import SchoolIcon from '@mui/icons-material/School';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import useAuth from '../../hooks/useAuth';
import { SIDEBAR_WIDTH, ROLES } from '../../utils/constants';
import ChangePasswordDialog from '../common/ChangePasswordDialog';

const ROLE_LABELS = {
  [ROLES.ADMIN]: 'Coordinator',
  [ROLES.FACULTY]: 'Faculty Advisor',
  [ROLES.OBSERVER]: 'Observer',
};

export default function TopBar({ onMenuClick }) {
  const { user, logout, role } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);

  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: isMobile ? '100%' : `calc(100% - ${SIDEBAR_WIDTH}px)`,
        ml: isMobile ? 0 : `${SIDEBAR_WIDTH}px`,
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Toolbar>
        {isMobile && (
          <IconButton edge="start" onClick={onMenuClick} sx={{ mr: 1 }}>
            <MenuIcon />
          </IconButton>
        )}

        {/* Co-branding: CWC mark + divider + college identity */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, flexGrow: 1, minWidth: 0 }}>
          {!isSmall && (
            <>
              <Box
                component="img"
                src="/images/cwc-logo-full.png"
                alt="Cyber Wise Corp"
                title="Cyber Wise Corp"
                sx={{ height: 36, objectFit: 'contain' }}
              />
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
            </>
          )}
          <Avatar
            src={user?.college_logo_url || undefined}
            alt={user?.college_name || 'College'}
            variant="rounded"
            sx={{
              width: 44,
              height: 44,
              bgcolor: user?.college_logo_url ? 'background.paper' : 'primary.light',
              border: '1px solid',
              borderColor: 'divider',
              '& img': { objectFit: 'contain', p: 0.4 },
            }}
          >
            {!user?.college_logo_url && <SchoolIcon fontSize="small" />}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', lineHeight: 1, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              College Portal
            </Typography>
            <Typography
              variant="h6"
              noWrap
              sx={{ color: 'text.primary', fontWeight: 700, lineHeight: 1.2, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}
              title={user?.college_name}
            >
              {user?.college_name || 'Cyber Wise Corp'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {!isSmall && role && (
            <Chip
              label={ROLE_LABELS[role] || role}
              size="small"
              sx={{
                backgroundColor: '#7c3aed18',
                color: '#7c3aed',
                fontWeight: 600,
                border: '1px solid #7c3aed40',
              }}
            />
          )}
          <Chip
            icon={<PersonIcon />}
            label={isSmall ? 'Account' : (user?.email || 'User')}
            variant="outlined"
            size="small"
            onClick={openMenu}
            sx={{ borderColor: 'primary.light', cursor: 'pointer', maxWidth: 220 }}
          />
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
            <MenuItem onClick={() => { closeMenu(); setPwOpen(true); }}>
              <ListItemIcon><LockResetIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Change Password</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { closeMenu(); logout(); }}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </AppBar>
  );
}
